/**
 * Geo Radius utilities.
 *
 * Filter search results by distance from a center point.
 * Uses Haversine formula to calculate distance between coordinates.
 *
 * Listing location: listing.attributes.geolocation = { lat, lng }
 * Filter: applied client-side after API response (or server-side via bbox approximation)
 */

const EARTH_RADIUS_KM = 6371;

/**
 * Calculate distance between two coordinates (Haversine formula).
 * @param {number} lat1 — degrees
 * @param {number} lng1 — degrees
 * @param {number} lat2 — degrees
 * @param {number} lng2 — degrees
 * @returns {number} distance in kilometers
 */
export const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => deg * (Math.PI / 180);
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Convert km radius to approximate bounding box (for Sharetribe bounds param).
 * @param {number} lat — center latitude
 * @param {number} lng — center longitude
 * @param {number} radiusKm
 * @returns {{ ne: {lat, lng}, sw: {lat, lng} }}
 */
export const radiusToBbox = (lat, lng, radiusKm) => {
  const latDelta = radiusKm / 111; // ~111km per degree latitude
  const lngDelta = radiusKm / (111 * Math.cos(lat * (Math.PI / 180)));
  return {
    ne: { lat: lat + latDelta, lng: lng + lngDelta },
    sw: { lat: lat - latDelta, lng: lng - lngDelta },
  };
};

/**
 * Filter listings by distance from a center point.
 * @param {Object[]} listings — Sharetribe listing objects
 * @param {number} lat
 * @param {number} lng
 * @param {number} radiusKm
 * @returns {Object[]}
 */
export const filterByRadius = (listings, lat, lng, radiusKm) => {
  if (!Array.isArray(listings) || !lat || !lng || !radiusKm) return listings;
  return listings.filter((listing) => {
    const geo = listing?.attributes?.geolocation;
    if (!geo?.lat || !geo?.lng) return false;
    return haversineDistance(lat, lng, geo.lat, geo.lng) <= radiusKm;
  });
};
